import {FileOutlined} from "../../../__snowpack__/pkg/@ant-design/icons.js";
import {notification, Upload as AntdUpload} from "../../../__snowpack__/pkg/antd.js";
import classNames from "../../../__snowpack__/pkg/classnames.js";
import React, {useEffect, useState} from "../../../__snowpack__/pkg/react.js";
import {
  basicFormRule,
  checkResolution,
  formatUnit,
  InvalidResolutionError,
  transferFileList,
  transferUploadFileArray
} from "./helper.js";
import "./index.css";
import {useIntl} from "../../../__snowpack__/pkg/react-intl.js";
import {SortableList} from "./SortableList.js";
import arrayMove from "../../../__snowpack__/pkg/array-move.js";
import {SessionHelper} from "../../../__snowpack__/pkg/@genie/lib-iam-utils.js";
export default function GnUpload(props) {
  const {
    id,
    dataShape = {name: "name", url: "url"},
    action,
    parseResponse = (response) => ({url: response?.data}),
    fileList,
    maxCount = Number.MAX_SAFE_INTEGER,
    maxSize = 5 * 1024 * 1024,
    resolution,
    disabled,
    readonly,
    emptyText = "-",
    children,
    showUploadList,
    className,
    direction,
    wrapperStyle = {},
    headers,
    beforeUpload,
    onChange,
    itemWrapperRender,
    ...restProps
  } = props;
  const {formatMessage} = useIntl();
  const [internalFileList, setInternalFileList] = useState(() => {
    if (!fileList) {
      return void 0;
    }
    if (fileList?.__internalFileList__) {
      return fileList?.__internalFileList__;
    }
    return transferFileList(fileList, dataShape);
  });
  useEffect(() => {
    if (internalFileList === void 0 && fileList?.__internalFileList__) {
      setInternalFileList(fileList.__internalFileList__);
      return;
    }
    if (!fileList) {
      setInternalFileList(void 0);
      return;
    }
    if (fileList?.__internalFileList__) {
      return;
    }
    const __internalFileList__ = transferFileList(fileList, dataShape);
    setInternalFileList(__internalFileList__);
    Object.defineProperty(fileList, "__internalFileList__", {writable: false, value: __internalFileList__});
  }, [fileList]);
  if (readonly && !fileList?.length) {
    return /* @__PURE__ */ React.createElement("span", null, emptyText);
  }
  return /* @__PURE__ */ React.createElement("div", {
    id,
    className: "gn-upload-wrapper",
    style: wrapperStyle
  }, /* @__PURE__ */ React.createElement(SortableList, {
    distance: 5,
    axis: "xy",
    items: internalFileList,
    uploadProps: props,
    helperClass: "gn-upload-item--dragging",
    onSortEnd: ({oldIndex, newIndex}) => {
      if (internalFileList) {
        const sortedFileList = arrayMove(internalFileList, oldIndex, newIndex);
        const file = sortedFileList[newIndex];
        setInternalFileList(sortedFileList);
        if (onChange) {
          const newFileList = transferUploadFileArray(sortedFileList, dataShape);
          onChange({file, fileList: newFileList});
        }
      }
    },
    onRemove: (file) => {
      if (internalFileList) {
        const filteredFileList = [...internalFileList.filter((item) => item.uid !== file.uid)];
        setInternalFileList(filteredFileList);
        if (onChange) {
          const newFileList = transferUploadFileArray(filteredFileList, dataShape);
          onChange({file, fileList: newFileList});
        }
      }
    }
  }), /* @__PURE__ */ React.createElement(AntdUpload, {
    className: classNames({
      "gn-upload": true,
      [`gn-upload-${direction}`]: !!direction,
      "gn-upload-readonly": readonly,
      [className]: !!className
    }),
    action,
    fileList: internalFileList,
    disabled: readonly || disabled,
    showUploadList: false,
    beforeUpload: (file, addedFileList) => {
      if (!addedFileList.hasFirstTimeHandled) {
        addedFileList.hasFirstTimeHandled = true;
        if (maxCount !== void 0) {
          const canceledFileList = addedFileList.splice(maxCount - (internalFileList?.length || 0));
          if (canceledFileList.length) {
            notification.error({
              message: formatMessage({id: "uploadFailed"}),
              style: {
                width: "max-content",
                minWidth: 350,
                maxWidth: 500,
                whiteSpace: "pre-wrap"
              },
              description: /* @__PURE__ */ React.createElement("div", null, formatMessage({id: "youCanUploadUpToMaxcount"}, {maxCount}), canceledFileList.map((_file, index) => {
                _file.removeTag = true;
                return /* @__PURE__ */ React.createElement("div", {
                  key: index,
                  style: {color: "red"}
                }, /* @__PURE__ */ React.createElement(FileOutlined, {
                  style: {marginRight: 4}
                }), _file.name);
              }))
            });
            setInternalFileList(internalFileList?.slice(maxCount));
          }
        }
      }
      if (file.removeTag) {
        file.status = "removed";
        return false;
      }
      if (file.size && file.size > maxSize) {
        file.response = formatMessage({id: "fileSizeExceedsLimitMaxsize"}, {maxSize: formatUnit(maxSize)});
        file.status = "error";
        notification.error({
          message: formatMessage({id: "uploadFailed"}),
          style: {width: "max-content"},
          description: formatMessage({id: "fileSizeExceedsLimitMaxsize"}, {maxSize: formatUnit(maxSize)})
        });
        return false;
      }
      if (resolution) {
        return checkResolution(file, resolution).catch((error) => {
          if (error instanceof InvalidResolutionError) {
            let defaultErrorMessage;
            if (error.type === "range") {
              defaultErrorMessage = formatMessage({id: "pictureResolutionExceedsTheLimit"});
            } else if (error.type === "ratio") {
              defaultErrorMessage = formatMessage({id: "theImageAspectRatioIsInvalid"}, {ratio: resolution.ratio});
            }
            file.response = error?.message || defaultErrorMessage;
            file.status = "error";
            notification.error({
              message: formatMessage({id: "uploadFailed"}),
              style: {width: "max-content", minWidth: 240, maxWidth: 400},
              description: file.response
            });
            throw error?.message;
          }
        });
      }
      return beforeUpload ? beforeUpload(file, addedFileList) : true;
    },
    onChange: (info) => {
      const _fileList = [...info.fileList];
      _fileList.forEach((file) => {
        if (file.status === "done" && file.response.code === "SUCCESS") {
          const res = parseResponse(file.response);
          Object.assign(file, res);
        }
        if (file.status === "done" && file.response.code !== "SUCCESS") {
          file.response = file.response.message;
          file.status = "error";
        }
        if (file.status === "error") {
          file.response = file.response?.message ?? formatMessage({id: "uploadFailed"});
        }
      });
      setInternalFileList(_fileList);
      if (onChange) {
        const newFileList = transferUploadFileArray(_fileList, dataShape);
        onChange({...info, fileList: newFileList});
      }
    },
    headers: {Authorization: SessionHelper.getSessionToken() ?? "", ...headers},
    ...restProps
  }, readonly ? void 0 : (internalFileList?.length ?? 0) >= maxCount ? null : children));
}
GnUpload.basicFormRule = basicFormRule;
